import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import { SCENE_H, SCENE_W } from '@rekrut/shared';
import { REASON_TEXT } from '../sim/engine.js';
import type { MatchState, Participant } from '../sim/state.js';
import type { AvatarCache } from './avatars.js';

function mkText(size: number, weight: string, color: number, center = false): Text {
  const t = new Text({
    text: '',
    style: new TextStyle({
      fontFamily: 'system-ui, "Noto Color Emoji", sans-serif',
      fontSize: size,
      fontWeight: weight as never,
      fill: color,
      stroke: { color: 0x05070c, width: Math.max(2, size * 0.12) },
    }),
  });
  if (center) t.anchor.set(0.5, 0);
  return t;
}

/**
 * Размер кадра. В альбомном режиме логическая сцена 1080×1920 разворачивается
 * в 1920×1080, и все полноэкранные плашки должны считаться от новых границ,
 * иначе итоги матча уезжают за край.
 */
let land = false;
const VW = (): number => (land ? SCENE_H : SCENE_W);
const VH = (): number => (land ? SCENE_W : SCENE_H);

/** Плашка крупного события на весь экран. */
class Banner {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly title = mkText(72, '900', 0xffffff, true);
  private readonly sub = mkText(30, '700', 0xd6e2f5, true);
  private until = 0;
  private startedAt = 0;
  private duration = 1;

  constructor() {
    this.layout();
    this.view.addChild(this.bg, this.title, this.sub);
    this.view.visible = false;
  }

  layout(): void {
    this.title.position.set(VW() / 2, VH() / 2 - 80);
    this.sub.position.set(VW() / 2, VH() / 2 + 14);
  }

  show(now: number, title: string, subtitle: string, color: number, ms: number): void {
    this.title.text = title;
    this.title.style.fill = color;
    this.sub.text = subtitle;
    this.startedAt = now;
    this.duration = ms;
    this.until = now + ms;
    this.view.visible = true;
  }

  update(now: number): void {
    if (!this.view.visible) return;
    if (now >= this.until) {
      this.view.visible = false;
      return;
    }
    const t = (now - this.startedAt) / this.duration;
    this.view.alpha = Math.max(0, Math.min(1, t < 0.12 ? t / 0.12 : t > 0.82 ? (1 - t) / 0.18 : 1));
    this.title.scale.set(t < 0.18 ? 1 + (0.18 - t) * 1.1 : 1);
    this.bg.clear();
    this.bg.rect(0, VH() / 2 - 150, VW(), 270).fill({ color: 0x05070c, alpha: 0.72 });
  }
}

/** Экран итогов: 20 секунд, ради которых стоит досмотреть штурм. */
class Results {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly body = new Container();
  private renderedFor = '';

  constructor(private readonly avatars: AvatarCache) {
    this.view.addChild(this.bg, this.body);
    this.view.visible = false;
  }

  /** Сбросить готовую вёрстку итогов: пересоберётся под новый размер кадра. */
  invalidate(): void {
    this.renderedFor = '';
  }

  update(s: MatchState): void {
    const show = s.phase === 'results';
    this.view.visible = show;
    if (!show) {
      this.renderedFor = '';
      return;
    }
    if (this.renderedFor !== s.matchId) {
      this.renderedFor = s.matchId;
      this.build(s);
    }
    const left = Math.max(0, Math.ceil((s.phaseEndsAt - s.now) / 1000));
    const timer = this.body.getChildByLabel('timer') as Text | null;
    if (timer) timer.text = `следующий штурм через ${left}`;
  }

  private build(s: MatchState): void {
    this.body.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.bg.clear();
    this.bg.rect(0, 0, VW(), VH()).fill({ color: 0x05070c, alpha: 0.9 });

    const winner = s.winner ? s.teams[s.winner] : null;
    const title = mkText(land ? 62 : 78, '900', winner?.color ?? 0xffffff, true);
    title.text = winner ? `${winner.label} ВЗЯЛИ ТРОН` : 'НИЧЬЯ';
    title.position.set(VW() / 2, land ? 70 : 280);

    const reason = mkText(28, '700', 0x8fa0bb, true);
    reason.text = s.endReason ? REASON_TEXT[s.endReason] : '';
    reason.position.set(VW() / 2, land ? 148 : 378);

    this.body.addChild(title, reason);

    const list = [...s.participants.values()];

    if (land) {
      // Кадр вчетверо ниже портретного: три блока встают в ряд, а не столбцом,
      // иначе последний уезжает за нижний край вместе с таймером.
      const y = 230;
      const half = 290;
      this.section(list, 'ТОП ПО УБИЙСТВАМ', (p) => p.kills, (p) => `${p.kills}`, y, 5, 340, half);
      this.section(list, 'ПО УРОНУ ТРОНУ', (p) => p.throneDamage, (p) => `${Math.round(p.throneDamage)}`, y, 5, VW() / 2, half);
      this.section(list, 'КОРОЛИ', (p) => (p.bestLevel >= 10 ? p.bestLevel : 0), () => '👑 10', y, 5, VW() - 340, half);
    } else {
      let y = 470;
      const cx = VW() / 2;
      const half = 330;
      y = this.section(list, 'ТОП ПО УБИЙСТВАМ', (p) => p.kills, (p) => `${p.kills}`, y, 5, cx, half);
      y = this.section(list, 'ПО УРОНУ ТРОНУ', (p) => p.throneDamage, (p) => `${Math.round(p.throneDamage)}`, y + 24, 3, cx, half);
      this.section(list, 'КОРОЛИ', (p) => (p.bestLevel >= 10 ? p.bestLevel : 0), () => '👑 10', y + 24, 3, cx, half);
    }

    const timer = mkText(26, '700', 0x6d7b95, true);
    timer.label = 'timer';
    timer.position.set(VW() / 2, VH() - (land ? 60 : 200));
    this.body.addChild(timer);
  }

  private section(
    list: Participant[],
    caption: string,
    by: (p: Participant) => number,
    fmt: (p: Participant) => string,
    y: number,
    n: number,
    /** Центр блока по горизонтали и его полуширина. */
    cx: number,
    half: number,
  ): number {
    const top = list.filter((p) => by(p) > 0).sort((a, b) => by(b) - by(a)).slice(0, n);
    const cap = mkText(24, '800', 0x8fa0bb, true);
    cap.text = caption;
    cap.position.set(cx, y);
    this.body.addChild(cap);
    if (top.length === 0) {
      const empty = mkText(24, '700', 0x4a5568, true);
      empty.text = '—';
      empty.position.set(cx, y + 36);
      this.body.addChild(empty);
      return y + 80;
    }
    top.forEach((p, i) => {
      const ry = y + 42 + i * 56;
      const av = new Sprite(this.avatars.get(p.userId, p.avatarUrl));
      av.anchor.set(0.5);
      av.width = av.height = 44;
      av.position.set(cx - half + 40, ry + 16);
      const name = mkText(28, '700', 0xe8eefb);
      name.text = `${i + 1}. ${p.nickname}`;
      name.position.set(cx - half + 82, ry);
      const val = mkText(28, '800', 0xffffff);
      val.anchor.set(1, 0);
      val.position.set(cx + half - 40, ry);
      val.text = fmt(p);
      this.body.addChild(av, name, val);
    });
    return y + 42 + top.length * 56;
  }
}

export class Overlays {
  readonly view = new Container();
  private readonly banner = new Banner();
  private readonly results: Results;

  constructor(avatars: AvatarCache) {
    this.results = new Results(avatars);
    this.view.addChild(this.results.view, this.banner.view);
  }

  /** Переключение ориентации кадра: все плашки считаются от новых границ. */
  setLandscape(on: boolean): void {
    land = on;
    this.banner.layout();
    this.results.invalidate();
  }

  showBanner(now: number, title: string, subtitle: string, color: number, ms: number): void {
    this.banner.show(now, title, subtitle, color, ms);
  }

  update(s: MatchState): void {
    this.results.update(s);
    this.banner.update(s.now);
  }
}
